import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi, usersApi, type LoginRequest, type RegisterRequest, type UpdateProfileRequest, type LoginResponse } from '@/api/auth'
import { authStorage } from '@/lib/auth-storage'

// ============================================
// Query Keys
// ============================================
export const authKeys = {
	all: ['auth'] as const,
	profile: () => [...authKeys.all, 'profile'] as const,
}

export const userKeys = {
	all: ['users'] as const,
	lists: () => [...userKeys.all, 'list'] as const,
	list: (params?: { page?: number; page_size?: number }) =>
		[...userKeys.lists(), params] as const,
}

// ============================================
// 登录 Hook
// ============================================
export function useLogin() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: LoginRequest) => authApi.login(data),
		onSuccess: (response: LoginResponse) => {
			// 使用 authStorage 存储 token
			authStorage.setToken(response.token)
			// 清除旧的用户信息缓存，等待重新获取
			queryClient.invalidateQueries({ queryKey: authKeys.profile() })
		},
	})
}

// ============================================
// 注册 Hook
// ============================================
export function useRegister() {
	return useMutation({
		mutationFn: (data: RegisterRequest) => authApi.register(data),
	})
}

// ============================================
// 获取当前用户信息 Hook
// ============================================
export function useProfile(enabled = true) {
	return useQuery({
		queryKey: authKeys.profile(),
		queryFn: authApi.getProfile,
		enabled,
		retry: false,
	})
}

// ============================================
// 更新用户信息 Hook
// ============================================
export function useUpdateProfile() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: UpdateProfileRequest) => authApi.updateProfile(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: authKeys.profile() })
		},
	})
}

// ============================================
// 修改密码 Hook
// ============================================
export function useUpdatePassword() {
	return useMutation({
		mutationFn: (data: import('@/api/auth').UpdatePasswordRequest) =>
			authApi.updatePassword(data),
	})
}

// ============================================
// 登出 Hook
// ============================================
export function useLogout() {
	const queryClient = useQueryClient()

	return () => {
		// 使用 authStorage 删除 token
		authStorage.removeToken()
		// 清除所有缓存
		queryClient.clear()
	}
}

// ============================================
// 用户列表 Hook
// ============================================
export function useUsers(params?: { page?: number; page_size?: number }) {
	return useQuery({
		queryKey: userKeys.list(params),
		queryFn: () => usersApi.list(params),
	})
}
